import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { ToastContext, type Toast, type ToastType } from './useToast';

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback((type: ToastType, message: string) => {
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 10)}`;
    setToasts(prev => [...prev, { id, type, message }]);

    const timer = window.setTimeout(() => {
      removeToast(id);
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [removeToast]);

  const success = useCallback((m: string) => addToast('success', m), [addToast]);
  const error = useCallback((m: string) => addToast('error', m), [addToast]);
  const warning = useCallback((m: string) => addToast('warning', m), [addToast]);
  const info = useCallback((m: string) => addToast('info', m), [addToast]);

  const value = useMemo(
    () => ({ toasts, addToast, removeToast, success, error, warning, info }),
    [toasts, addToast, removeToast, success, error, warning, info],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
}
